import { Autocomplete, Box, Chip, IconButton, Tab, Tabs, TextField, Tooltip } from "@mui/material";
import MapIcon from "@mui/icons-material/Map";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import React, { useMemo, useState } from "react";

import { useArchivedProjects, useProjects } from "../../api/projects";
import useDebounce from "../../hooks/useDebounce";
import ProjectsListingMap from "../ProjectListingMap";
import ProjectsListingTiled from "../ProjectListingTiled";
import ArchivedProjectsGrid from "./ArchivedProjectsGrid";

interface ProjectListingClassicProps {
  /** The control that switches back to the new layout. */
  layoutToggle: React.ReactNode;
}

/**
 * The pre-folders listing page, kept as an escape hatch: a tile grid or a
 * full-width map behind an icon toggle, with search and tag filters. Nothing
 * here knows about folders.
 */
export default function ProjectListingClassic({ layoutToggle }: ProjectListingClassicProps) {
  const [view, setView] = useState<"tile" | "map">("tile");
  const [activeTab, setActiveTab] = useState<"active" | "archived">("active");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const { projects } = useProjects(debouncedSearchQuery, selectedTags.join(","));
  const { projects: archivedProjects, isLoading: archivedLoading } = useArchivedProjects(
    debouncedSearchQuery,
    selectedTags.join(",")
  );

  // Extract tags from current projects for the filter dropdown
  const currentTags = useMemo(() => {
    if (!projects?.data) return [];
    const tagSet = new Set<string>();
    projects.data.forEach((project) => {
      if (project.tags) {
        project.tags.forEach((tag) => tagSet.add(tag));
      }
    });
    return Array.from(tagSet).sort();
  }, [projects]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "var(--vh100)" }}>
      <Box sx={{ borderBottom: 1, borderColor: "divider", px: 5 }}>
        <Tabs value={activeTab} onChange={(_e, v: "active" | "archived") => setActiveTab(v)}>
          <Tab label="Active" value="active" />
          <Tab label="Archived" value="archived" />
        </Tabs>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "end",
          alignItems: "center",
          marginRight: 5,
          gap: 2,
          marginBottom: 2,
          marginTop: 2,
        }}
      >
        {layoutToggle}
        <Tooltip title="View projects as tiles">
          <IconButton onClick={() => setView("tile")} color={view === "tile" ? "primary" : "default"}>
            <ViewModuleIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="View projects on map">
          <IconButton onClick={() => setView("map")} color={view === "map" ? "primary" : "default"}>
            <MapIcon />
          </IconButton>
        </Tooltip>
        <TextField
          size="small"
          placeholder="Search projects..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ width: 300 }}
        />
        <Autocomplete
          multiple
          size="small"
          options={currentTags}
          value={selectedTags}
          onChange={(_e, v: string[]) => setSelectedTags(v)}
          renderInput={(params) => (
            <TextField {...params} placeholder="Filter by tags..." sx={{ width: 300 }} />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option}
                label={option}
                size="small"
                sx={{ backgroundColor: "#e0f2fe", color: "#0369a1" }}
              />
            ))
          }
        />
      </Box>
      <Box sx={{ flexGrow: 1, alignContent: "flex-start", width: "100%", height: "100%" }}>
        {activeTab === "active" && (
          <>
            {view === "tile" && (
              <ProjectsListingTiled searchQuery={debouncedSearchQuery} tagFilter={selectedTags.join(",")} />
            )}
            {view === "map" && (
              <ProjectsListingMap searchQuery={debouncedSearchQuery} tagFilter={selectedTags.join(",")} />
            )}
          </>
        )}
        {activeTab === "archived" && (
          <ArchivedProjectsGrid projects={archivedProjects?.data} isLoading={archivedLoading} />
        )}
      </Box>
    </Box>
  );
}
